import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePDF } from 'react-to-pdf';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Download, 
  Save,
  Calendar
} from 'lucide-react';
import { apiService } from '@/services/api';
import { 
  ReportDisplay,
  ApiWidget,
  WidgetConfig,
  ReportData,
  ReportTheme
} from './Generate/components';
import SubSidebar from '@/components/layout/SubSidebar';

// Process API response data according to widget data_points configuration
const processWidgetData = (rawData: any, dataPoints: string[], presentation: string) => {

  
  if (!rawData) {
    console.log('No raw data, returning null');
    return null;
  }

  // Return the raw response data directly without processing
  return rawData;
};

// Generate real data from analytics data
const generateRealData = (widgetId: string, presentation: string, analyticsData: any) => {
  if (!analyticsData) {
    return null;
  }

  // Use real analytics data for specific metrics
  switch (widgetId) {
    case 'total-sessions':
      return analyticsData.sessions || null;
    case 'total-users':
      return analyticsData.visitors || null;
    case 'total-pageviews':
      return analyticsData.pageViews || null;
    case 'bounce-rate':
      return analyticsData.bounce_rate || null;
    case 'sessions-over-time':
      return null; // Would need time series data from API
    case 'users-over-time':
      return null; // Would need time series data from API
    case 'pageviews-over-time':
      return null; // Would need time series data from API
    case 'bounce-rate-trend':
      return null; // Would need time series data from API
    case 'top-pages':
      return null; // Would need pages data from API
    case 'traffic-sources':
      return null; // Would need traffic sources data from API
    default:
      return null;
  }
};



const GenerateReport: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const reportData: ReportData = location.state?.reportData || {};
  
  const [availableWidgets, setAvailableWidgets] = useState<ApiWidget[]>([]);
  const [widgets, setWidgets] = useState<WidgetConfig[]>([]);
  const [draggedWidget, setDraggedWidget] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingWidgets, setIsLoadingWidgets] = useState(true);
  const [selectedWidgetIds, setSelectedWidgetIds] = useState<Set<string>>(new Set());

  // Theme settings - local theme for report only
  const [reportTheme, setReportTheme] = useState<ReportTheme>({
    theme: 'dark',
    primaryColor: '#3b82f6'
  });

  // Right panel visibility
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(false);

  // PDF generation
  const { toPDF, targetRef } = usePDF({
    filename: `analytics-report-${new Date().toISOString().split('T')[0]}.pdf`,
    page: {
      margin: 20,
      format: 'a4',
      orientation: 'portrait',
    },
  });

  // Fetch available widgets from API
  useEffect(() => {
    const fetchWidgets = async () => {
      try {
        setIsLoadingWidgets(true);
        const response = await apiService.get('/analytics/report-widgets/');
        let widgetsData: ApiWidget[] = [];
        
        // Handle the API response structure: { success: true, widgets: [...] }
        if (response.data?.success && Array.isArray(response.data.widgets)) {
          widgetsData = response.data.widgets as ApiWidget[];
        } else if (Array.isArray(response.data?.data)) {
          widgetsData = response.data.data as ApiWidget[];
        } else if (Array.isArray(response.data)) {
          widgetsData = response.data as ApiWidget[];
        }
        // If API returns empty data, show empty state
        if (widgetsData.length === 0) {
          console.warn('API returned empty widget data');
          setAvailableWidgets([]);
          setWidgets([]);
          setSelectedWidgetIds(new Set());
          return;
        }
        
        setAvailableWidgets(widgetsData);
        
        // Initialize with all widgets from the API as default
        const initialWidgets = await Promise.all(
          widgetsData.map(async (widget: ApiWidget, index: number) => {
            let data = null;
            try {
              // Use the api_endpoint from the widget configuration
              const response = await apiService.get(widget.api_endpoint);
              
              // Process the data according to widget's data_points configuration
              data = processWidgetData(response.data, widget.data_points, widget.presentation);
              
              // If processing failed, set data to null
              if (!data) {
                console.warn(`Widget ${widget.id} data processing failed, no data available`);
                data = generateRealData(widget.id, widget.presentation, reportData.analyticsData);
              }
            } catch (error) {
              console.error(`Error fetching data for widget ${widget.id} from ${widget.api_endpoint}:`, error);
              data = generateRealData(widget.id, widget.presentation, reportData.analyticsData);
            }
            
            return {
              ...widget,
              sortOrder: index + 1,
              data
            };
          })
        );
        setWidgets(initialWidgets);
        setSelectedWidgetIds(new Set(initialWidgets.map(w => w.id)));
      } catch (error) {
        console.error('Error fetching widgets:', error);
        // If API fails, show empty state - no fallback widgets
        setAvailableWidgets([]);
        setWidgets([]);
        setSelectedWidgetIds(new Set());
      } finally {
        setIsLoadingWidgets(false);
      }
    };

    fetchWidgets();
  }, [reportData.analyticsData]);


  const handleDragStart = (e: React.DragEvent, widgetId: string) => {
    setDraggedWidget(widgetId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetWidgetId: string) => {
    e.preventDefault();
    if (!draggedWidget || draggedWidget === targetWidgetId) return;

    setWidgets(prev => {
      const draggedWidgetData = prev.find(w => w.id === draggedWidget);
      const targetWidgetData = prev.find(w => w.id === targetWidgetId);
      
      if (!draggedWidgetData || !targetWidgetData) return prev;

      return prev.map(widget => {
        if (widget.id === draggedWidget) {
          return { ...widget, sortOrder: targetWidgetData.sortOrder };
        } else if (widget.id === targetWidgetId) {
          return { ...widget, sortOrder: draggedWidgetData.sortOrder };
        }
        return widget;
      });
    });

    setDraggedWidget(null);
  };

  const handleDeleteWidget = (widgetId: string) => {
    setWidgets(prev => prev.filter(widget => widget.id !== widgetId));
    setSelectedWidgetIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(widgetId);
      return newSet;
    });
  };

  const handleAddWidget = async (widget: ApiWidget) => {
    const maxSortOrder = Math.max(...widgets.map(w => w.sortOrder), 0);
    
    let data = null;
    try {
      // Use the api_endpoint from the widget configuration
      const response = await apiService.get(widget.api_endpoint);
      console.log(`Widget ${widget.id} API response:`, response.data);
      
      // Process the data according to widget's data_points configuration
      data = processWidgetData(response.data, widget.data_points, widget.presentation);
      
      // If processing failed, set data to null
      if (!data) {
        console.warn(`Widget ${widget.id} data processing failed, no data available`);
        data = generateRealData(widget.id, widget.presentation, reportData.analyticsData);
      }
    } catch (error) {
      console.error(`Error fetching data for widget ${widget.id} from ${widget.api_endpoint}:`, error);
      data = generateRealData(widget.id, widget.presentation, reportData.analyticsData);
    }
    
    const newWidget: WidgetConfig = {
      ...widget,
      sortOrder: maxSortOrder + 1,
      data
    };
    setWidgets(prev => [...prev, newWidget]);
    setSelectedWidgetIds(prev => new Set(Array.from(prev).concat(widget.id)));
  };

  const handleRemoveWidget = (widgetId: string) => {
    setWidgets(prev => prev.filter(widget => widget.id !== widgetId));
    setSelectedWidgetIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(widgetId);
      return newSet;
    });
  };

  const handleWidgetSelection = (widget: ApiWidget, checked: boolean) => {
    if (checked) {
      handleAddWidget(widget);
    } else {
      handleRemoveWidget(widget.id);
    }
  };

  const handleFinishReport = async () => {
    setIsGenerating(true);
    
    // Simulate API call to save report
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Navigate back to reports list
    navigate('/reports');
    
    setIsGenerating(false);
  };

  const handleDownloadPDF = () => {
    toPDF();
  };

  const handleCustomizeClick = () => {
    setIsRightPanelOpen(true);
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Generate Report</h1>
          <p className="text-muted-foreground">
            Customize your report layout and content
          </p>
          {reportData.dateRange && (
            <div className="flex items-center gap-2 mt-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <Badge variant="outline">
                {reportData.dateRange === 'custom' 
                  ? `${reportData.startDate} to ${reportData.endDate}`
                  : reportData.dateRange === 'month'
                  ? reportData.selectedMonth
                  : reportData.dateRange
                }
              </Badge>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleDownloadPDF}>
            <Download className="mr-2 h-4 w-4" />
            Download PDF
          </Button>
          <Button onClick={handleFinishReport} disabled={isGenerating}>
            <Save className="mr-2 h-4 w-4" />
            {isGenerating ? 'Saving...' : 'Finish Report'}
          </Button>
        </div>
      </div>

      {/* Report Display */}
      <ReportDisplay
            reportData={reportData}
            reportTheme={reportTheme}
            widgets={widgets}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDeleteWidget={handleDeleteWidget}
            onCustomizeClick={handleCustomizeClick}
            targetRef={targetRef}
          />

      {/* Sub Sidebar */}
      <SubSidebar
        availableWidgets={availableWidgets}
        selectedWidgetIds={selectedWidgetIds}
        isLoadingWidgets={isLoadingWidgets}
        reportTheme={reportTheme}
        isOpen={isRightPanelOpen}
        onWidgetSelection={handleWidgetSelection}
        onThemeChange={setReportTheme}
        onClose={() => setIsRightPanelOpen(false)}
      />
    </div>
  );
};

export default GenerateReport;
